import { z } from "zod";

// Job Status Types and Utilities
export type JobStatus = 'quote-needed' | 'quoted' | 'accepted' | 'in-progress' | 'complete' | 'invoiced' | 'paid' | 'archived';

export const jobStatusEnum = z.enum([
  'quote-needed',    // RED - Default status when job is created
  'quoted',          // ORANGE - Quote has been sent
  'accepted',        // GREEN - Quote has been accepted
  'in-progress',     // LIGHT BLUE - Job is scheduled and in progress
  'complete',        // DARK BLUE - Job work is completed (manual)
  'invoiced',        // PURPLE - Invoice has been sent
  'paid',            // PINK - Payment has been received
  'archived',        // GREY - Job archived after 3 days in paid status
]);

export const VALID_STATUS_TRANSITIONS: Record<JobStatus, JobStatus[]> = {
  'quote-needed': ['quoted'],           // Quote sent → quoted
  'quoted': ['accepted'],               // Quote accepted → accepted
  'accepted': ['in-progress'],          // Job scheduled → in-progress
  'in-progress': ['complete'],          // Work completed → complete (manual)
  'complete': ['invoiced'],            // Invoice sent → invoiced
  'invoiced': ['paid'],                 // Payment received → paid
  'paid': ['archived'],                 // After 3 days → archived (automatic)
  'archived': [],                       // No transitions from archived
};

export function normalizeLegacyStatus(status: string): string {
  const legacyMap: Record<string, JobStatus> = {
    // Old statuses → New statuses
    'unscheduled': 'quote-needed',
    'scheduled': 'accepted',            // Scheduled jobs were likely accepted
    'completed': 'complete',
    'booked': 'in-progress',            // Booked jobs are in progress
    'complete': 'complete',
    'quote-required': 'quote-needed',
    'quote-pending': 'quoted',          // Quote pending means it was sent
    'accepted': 'accepted',
    'invoiced': 'invoiced',
    'paid': 'paid',
    'in-progress': 'in-progress',
  };
  // If status is already a valid new status, return as-is
  if (jobStatusEnum.safeParse(status).success) {
    return status;
  }
  return legacyMap[status.toLowerCase()] || 'quote-needed'; // Default to quote-needed
}

export function isValidStatusTransition(currentStatus: string, newStatus: string): boolean {
  const normalizedCurrent = normalizeLegacyStatus(currentStatus);
  const normalizedNew = normalizeLegacyStatus(newStatus);
  if (!jobStatusEnum.safeParse(normalizedCurrent).success || !jobStatusEnum.safeParse(normalizedNew).success) {
    return false;
  }
  if (normalizedCurrent === normalizedNew) {
    return true;
  }
  const allowedTransitions = VALID_STATUS_TRANSITIONS[normalizedCurrent as JobStatus];
  return allowedTransitions.includes(normalizedNew as JobStatus);
}

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  'quote-needed': 'Quote Needed',
  'quoted': 'Quoted',
  'accepted': 'Accepted',
  'in-progress': 'In Progress',
  'complete': 'Complete',
  'invoiced': 'Invoiced',
  'paid': 'Paid',
  'archived': 'Archived',
};

export const JOB_STATUS_COLORS: Record<JobStatus, string> = {
  'quote-needed': '#EF4444',   // RED
  'quoted': '#F97316',         // ORANGE
  'accepted': '#10B981',       // GREEN
  'in-progress': '#3B82F6',    // LIGHT BLUE
  'complete': '#1E40AF',       // DARK BLUE
  'invoiced': '#A855F7',       // PURPLE
  'paid': '#EC4899',           // PINK
  'archived': '#6B7280',       // GREY
};

export function getJobStatusLabel(status: string): string {
  const normalized = normalizeLegacyStatus(status) as JobStatus;
  return JOB_STATUS_LABELS[normalized] || status;
}

export function getJobStatusColor(status: string): string {
  const normalized = normalizeLegacyStatus(status) as JobStatus;
  return JOB_STATUS_COLORS[normalized] || '#6B7280';
}

// Job Interface Types
export interface Job {
  id: string;
  tenantId: string;
  jobNumber: string | null;
  customerId: string;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  assignedTo: string[] | null;
  assignedStaffIds: string[] | null;
  scheduledStartDate: Date | null;
  scheduledEndDate: Date | null;
  actualStartDate: Date | null;
  actualEndDate: Date | null;
  address: string | null;
  latitude: string | null;
  longitude: string | null;
  estimatedDuration: number | null;
  estimatedHours: number | null;
  notes: string | null;
  quoteRequired: boolean | null;
  recurringSchedule: unknown | null;
  checklistItems: unknown[] | null;
  tags: string[] | null;
  reviewRequestSent: boolean | null;
  invoiceId: string | null;
  invoiceStatus: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  archivedAt: Date | null;
  clientName?: string;
  arrivalStatus?: string;
  eta?: string | Date | null;
  arrivedAt?: Date | null;
  leftAt?: Date | null;
  staffArrivals?: Record<string, { arrivedAt: string; leftAt?: string }>; // Track arrivals per staff
  assignedStaffId?: string;
  isRepeating?: boolean;
  jobTemplateId?: string | null;
}

export interface JobLineItem {
  id: string;
  tenantId: string;
  jobId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  total: string;
  itemId: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface JobNote {
  id: string;
  tenantId: string;
  jobId: string;
  userId: string | null;
  content: string;
  isInternal: boolean | null;
  createdAt: Date | null;
  note?: string;
}

export interface JobFile {
  id: string;
  tenantId: string;
  jobId: string;
  userId: string | null;
  fileName: string;
  fileUrl: string;
  fileType: string | null;
  fileSize: number | null;
  createdAt: Date | null;
  uploadedBy?: string;
  transcription?: string;
}

export interface JobChecklistItem {
  id: string;
  tenantId: string;
  jobId: string;
  text: string;
  isCompleted: boolean | null;
  completedBy: string | null;
  completedAt: Date | null;
  sortOrder: number | null;
  createdAt: Date | null;
  title?: string;
  description?: string;
}

export interface InsertJob {
  tenantId: string;
  customerId: string;
  title: string;
  description?: string | null;
  status?: string;
  priority?: string | null;
  assignedTo?: string[] | null;
  scheduledStartDate?: Date | null;
  scheduledEndDate?: Date | null;
  actualStartDate?: Date | null;
  actualEndDate?: Date | null;
  address?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  estimatedDuration?: number | null;
  notes?: string | null;
  quoteRequired?: boolean | null;
  recurringSchedule?: unknown | null;
  checklistItems?: unknown[] | null;
  tags?: string[] | null;
}

// Job Template Content Schema
export const jobTemplateContentSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  priority: z.string().optional(),
  estimatedDuration: z.number().optional(),
  estimatedHours: z.number().optional(),
  defaultRate: z.number().optional(),
  checklistItems: z.array(z.string()).optional(),
  notes: z.string().optional(),
  tags: z.array(z.string()).optional(),
  status: z.string().optional(),
  assignedStaffIds: z.array(z.string()).optional(),
  customerId: z.string().optional(),
  scheduledStartDate: z.string().optional(),
  scheduledEndDate: z.string().optional(),
  address: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

export type JobTemplateContent = z.infer<typeof jobTemplateContentSchema>;

